<template>
  <div class="admin-distributors">
    <header>
      <div>
        <h1>{{ $t('global.distributors') }}</h1>
        <small>{{ meta.totalCount || 0 }} in total</small>
      </div>
      <div class="filters-actions">
        <ez-filter-list
          :filters="filters"
          @resetFilter="resetFilters"
          @filterUpdated="updateFilters"
        >
          <ez-input
            formKey="filters"
            label="search"
            name="search"
            class="search"
            :placeholder="`Search for a ${$t('global.distributor')}`"
            :data-cy="``"
          >
            <template #prefix>
              <font-awesome-icon icon="search" />
            </template>
          </ez-input>
          <ez-dropdown name="accountType" formKey="filter-accType" :data="accTypeData" />
        </ez-filter-list>

        <router-link
            :to="{ name: 'admin-distributors-new' }"
            class="button button--primary"
        >
          <span>Add New {{ $t('global.distributor') }}</span>
        </router-link>
      </div>
    </header>
    <ez-table
      :data="distributors"
      :loading="isLoading"
      :columns="['name', 'registeredAt', 'productCount', 'status']"
      :headers="headers"
      :columnProps="{
        registeredAt: { class: 'date-cell' },
        productCount: { class: 'medium-cell' },
        status: { class: 'medium-cell' },
      }"
      @rowClick="openDistributor"
      class="pb-12"
    >
      <template #cell-name="{ row }">
        <v-distributor-entity-info :distributor="row" />
      </template>

      <template #cell-registeredAt="{ row :{registeredAt}}">
        <div v-if="registeredAt">{{ registeredAt | date }}</div>
        <div v-else>-</div>
      </template>

      <template #cell-productCount="{ row: { productCount } }">
        <div class="text-color--dark">{{ productCount || 0 }}</div>
      </template>
      <template #cell-status="{ row: { status } }">
        <status-badge :status="status" />
      </template>
    </ez-table>

    <div v-if="isLoadingMore" class="u-text-center mt-12">
      <ez-spinner />
    </div>

    <ez-load-more v-if="meta.nextId && !isLoadingMore" @loadMore="fetchMoreDistributors" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { distributorAccountType, LOADING_KEY } from '@/util/constants';
import EzLoadMore from '@/components/ui/LoadMore/EzLoadMore.vue';
import EzSpinner from '@/components/ui/Spinner/EzSpinner.vue';
import EzTable from '@/components/ui/Table';
import VDistributorEntityInfo from '@/components/v3/patterns/VDistributorEntityInfo';
import StatusBadge from '@/views/common/status-badge';
import EzInput from '@/components/ui/Input/EzInput.vue';
import EzFilterList from '@/components/ui/FilterList/EzFilterList.vue';
import { debounce } from '@/util/utils';
import EzDropdown from '@/components/ui/Dropdown/EzDropdown.vue';

export default {
  components: {
    EzDropdown,
    EzFilterList,
    EzInput,
    EzLoadMore,
    EzSpinner,
    EzTable,
    VDistributorEntityInfo,
    StatusBadge,
  },
  data() {
    return {
      createFormKey: 'create-distributor',
      headers: {
        name: () => `${this.$t('global.distributors')} name`,
        email: () => 'Email address',
        registeredAt: () => 'Date registered',
        productCount: () => '# of products',
        status: h => h('div', { style: { textAlign: 'right' } }, 'Status'),
      },
      distributors: [],
      meta: {},
      filters: {
        term: null,
      },
    };
  },
  computed: {
    ...mapGetters('loading', ['getLoading']),
    isLoading() {
      return this.getLoading(LOADING_KEY.FETCH_ADMIN_DISTRIBUTORS);
    },
    isLoadingMore() {
      return this.getLoading(LOADING_KEY.FETCH_MORE_ADMIN_DISTRIBUTORS);
    },
    accTypeData() {
      return [
        {
          id: null,
          name: 'All Accounts',
        },
        ...distributorAccountType,
      ];
    },
  },
  methods: {
    ...mapActions('entities/distributors', ['fetchAdminDistributors']),
    restData() {
      this.distributors = [];
      this.meta = {};
    },
    async resetFilters() {
      Object.keys(this.filters).forEach((key) => { this.filters[key] = null; });
      this.restData();
      await this.fetchDistributors();
    },
    updateFilters: debounce(async function deb(filterName, event) {
      if (filterName === 'search') {
        if (event.length !== 0 && event.length < 3) return;
        this.filters = { ...this.filters, term: event.length ? event : null };
      } else {
        this.filters = { ...this.filters, [filterName]: event.id };
      }

      this.restData();
      await this.fetchDistributors();
    }, 300),
    openDistributor({ id }) {
      this.$router.push({
        name: 'admin-distributor-info',
        params: { id },
      });
    },
    async fetchDistributors(loadingKey = LOADING_KEY.FETCH_ADMIN_DISTRIBUTORS) {
      const query = {
        ...(this.meta.nextId && { nextId: this.meta.nextId }),
        ...(this.meta.nextValue && { nextValue: this.meta.nextValue }),
        limit: 20,
        ...this.filters,
      };
      const { data } = await this.fetchAdminDistributors({ query, loadingKey });
      this.distributors = [...this.distributors, ...data.data];
      this.meta = data.meta;
    },
    async fetchMoreDistributors() {
      await this.fetchDistributors(LOADING_KEY.FETCH_MORE_ADMIN_DISTRIBUTORS);
    },
  },
  async mounted() {
    await this.fetchDistributors();
  },
};
</script>

<style scoped lang="scss">
$default-status-color: #8790A3;
$active-status-color: #3BBD8E;

.admin-distributors {
  @extend %list;
  height: auto;
  overflow: unset;

  :deep() header {
    flex-direction: column;
    align-items: flex-start;

    h1 {
      @include font-size(24px);
      font-weight: 500;
      margin: 0;
    }

    small {
      @include font-size(12px);
      font-weight: 500;
      margin-top: 0.25rem;
      color: $color-gray-6C;
    }

    .filters-actions {
      width: 100%;
      padding-top: 24px;
      display: flex;
      justify-content: space-between;
    }
  }

  a.button {
    @extend %base-button;
    @include button-type('primary',
      $button-primary-bg,
      $button-primary-color,
      $button-primary-hover-bg,
      $button-primary-color
    ) {
      &:hover {
        background-color: $button-primary-hover-bg;
      }
      &:active {
        background-color: $button-primary-bg;
      }
      text-decoration: none;
    };
  }

  :deep()  {
    .ez-filter-list {
      flex-grow: 1;
    }
  }

  .distributor-info {
    @include name-status-group();
    justify-content: center;
  }

  .distributor-form {
    margin: 1rem auto;
  }
}
</style>
